import { get } from 'lodash'
import { createSelector } from 'reselect'

// Selectors
import {
  selectHomebotListingsFeature,
  selectHasYlopoListingsFeature,
  selectCustomerProfileLoading,
  selectCustomerEmail,
  selectCustomerId,
} from 'src/store/selectors/customerProfile'
import {
  selectClientIsGuest,
  selectClientIsLoadingData,
} from 'src/store/selectors/client'

// Constants
import {
  LISTINGS_PURCHASING_POWER,
  LISTINGS_SIMILAR_HOMES,
  LISTINGS_EQUITY_SMART_SEARCH,
  YLOPO_LISTINGS_FEATURE,
  HOMEBOT_LISTINGS_FEATURE,
  LISTINGS_ZIP_CODE,
  LISTINGS_FAVORITES,
  LISTINGS_SEARCH,
  LISTING_MARKERS_SEARCH,
} from 'src/constants/listings'

// Helpers
import { isYlopoLoaded } from 'src/helpers/ylopo'
import { snakeCaseKeys } from 'src/helpers/utils'

export const selectListing = state => state.listings?.listing || null

export const selectSimilarListings = state => state.listings.similarListings

export const selectSimilarHomesListings = state =>
  state.listings.listings[LISTINGS_SIMILAR_HOMES] || null

export const selectPurchasePowerHomeListings = state =>
  state.listings.listings[LISTINGS_PURCHASING_POWER] || null

export const selectEquitySmartSearchListings = state =>
  state.listings.listings[LISTINGS_EQUITY_SMART_SEARCH] || null

export const selectFavoriteListings = state =>
  state.listings.listings[LISTINGS_FAVORITES] || null

export const selectFavoriteListingsLoading = state =>
  state.listings.loading[LISTINGS_FAVORITES]

export const selectFormattedListingItems = createSelector(
  selectListing,
  listing => {
    const capitalize = word => word.charAt(0).toUpperCase() + word.substring(1)
    const format = string => {
      const words = string.replace('has', '').match(/[A-Za-z][a-z]*/g) || []

      return words.map(capitalize).join(' ')
    }

    if (listing.id) {
      const items = Object.keys(listing.has).filter(key => listing.has[key])
      const formattedItems = items.map(item => format(item))
      formattedItems.push(...(listing.utilities || []))
      formattedItems.push(...(listing.utilitiesBuilding || []))

      return formattedItems
    }
    return []
  }
)

export const selectHomebotListingsSearchLoading = state =>
  state.listings.loading[LISTINGS_SEARCH] || false

export const selectHomebotListingsMarkerSearchLoading = state =>
  state.listings.loading[LISTING_MARKERS_SEARCH] || false

export const selectHomebotListingsLoading = state =>
  state.listings?.loading?.listings

export const selectFavoriteListingIds = createSelector(
  selectFavoriteListings,
  favoriteListings =>
    favoriteListings?.map(favoriteListing => favoriteListing.id) || null
)

export const selectSearchListings = state =>
  state.listings.listings[LISTINGS_SEARCH] || null

export const selectSearchListingsTotal = state =>
  state.listings.searchListingsTotal

export const selectSelectedListingId = state => state.listings.selectedListingId

export const selectSearchListingMarkers = state =>
  state.listings.listings[LISTING_MARKERS_SEARCH] || null

export const selectZipCodeListings = state =>
  state.listings.listings[LISTINGS_ZIP_CODE] || null

export const selectShowListingSearchModal = state =>
  state.listings.uiFlags.showSearchModal

export const selectYlopoUrl = state => state.listings.ylopoUrl

export const selectListingsSource = state =>
  get(state, 'listings.listingsSource')

export const selectListingsSourceIsHomebotListings = createSelector(
  selectListingsSource,
  source => source === HOMEBOT_LISTINGS_FEATURE
)

export const selectListingsSourceIsYlopo = createSelector(
  selectListingsSource,
  source => source === YLOPO_LISTINGS_FEATURE
)

export const selectDisplayYlopoSpecificContent = createSelector(
  selectListingsSourceIsYlopo,
  selectHasYlopoListingsFeature,
  (ylopoSrc, hasYlopoFeature) => {
    if (ylopoSrc && hasYlopoFeature) {
      const display = isYlopoLoaded()

      return display
    }
    return false
  }
)

export const selectHasListingsEnabled = createSelector(
  selectHomebotListingsFeature,
  selectDisplayYlopoSpecificContent,
  (hasHomebotListingsEnabled, hasYlopoFeatureEnabled) => {
    if (hasHomebotListingsEnabled || hasYlopoFeatureEnabled) {
      return true
    }
    return false
  }
)

export const selectListingsSearchIsLoading = createSelector(
  selectCustomerProfileLoading,
  selectClientIsLoadingData,
  selectClientIsGuest,
  (customerProfileLoading, clientIsLoadingData, isGuest) =>
    customerProfileLoading || (clientIsLoadingData && !isGuest)
)

// Mikasa expects listing object for DMs to be snake_cased
// This formats the listing object to snake_case
export const selectListingDMMetadata = createSelector(
  selectCustomerId,
  selectCustomerEmail,
  selectListing,
  (id, email, listing) => ({
    customer_id: id,
    email,
    listing: snakeCaseKeys(listing),
  })
)
